<template>
    <div class="vision-brief-container" v-if="visionStatus === 'static'">
        <div class="static-qn-container">
            <div class="static-qn-wrapper">
                <div class="vision-form" :class="{ selected: (selectedInput == '' || selectedInput !== null) }"
                    @submit.prevent>
                    <div class="top-container">
                        <p class="static-qn">{{ staticQuestion[0].question }}</p>
                        <p class="sub-txt">Choose one option below. Once selected, you'll move to the next step to
                            generate
                            vision themes.</p>
                    </div>

                    <div class="bottom-container">
                        <div class="btn-wrapper" :class="{ active: selectedInput === 'autoGenerate' }">
                            <button class="toggle-input-btn" @click="toggleButton('autoGenerate')">
                                <Svg :src="StartIcon"></Svg>
                                <p class="btn-txt">Auto-Generate using Context and Strategy</p>
                            </button>
                        </div>

                        <div class="btn-wrapper" :class="{ active: selectedInput === 'inputBrief' }">
                            <button class="toggle-input-btn" @click="toggleButton('inputBrief')">
                                <Svg :src="InputBrief"></Svg>
                                <p class="btn-txt">Write your own Vision Brief</p>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="selectedInput === 'inputBrief'" class="input-container vision-brief">
                    <p class="input-txt">Vision Brief</p>
                    <div class="vision-static-form">
                        <div class="label-bold">{{ staticQuestion[0].question }}</div>
                        <div class="static-container">
                                <div class="qn-header">
                                    <div class="header-left">
                                        <Svg height="22" :src="InfoIcon"></Svg>
                                        <p class="header-txt">Expert Input Guide</p>
                                    </div>
                                    <button class="dropdown" @click="isOpen = !isOpen">
                                        <Svg height="9" v-if="isOpen" width="12" :src="UpIcon"></Svg>
                                        <Svg height="9" v-else width="12" :src="DownIcon" ></Svg>
                                    </button>
                                </div>
                                <div v-if="isOpen" class="qn-wrapper">
                                        <p class="header-txt">What's the bigger impact?</p>
                                        <p class="qn-txt">{{ questions }}</p>
                                </div>
                        </div>
                        <textarea name="w3review" rows="10" class="form-input" v-model="staticQuestion[0].answer"
                            placeholder="[Start by describing the problem, your approach, and the impact you want to create…]"></textarea>
                    </div>

                </div>

                <div v-else-if="selectedInput === 'autoGenerate'" class="input-container">
                    <Svg :src="TickIcon"></Svg>
                    <p class="input-txt">Your vision will be generated using the context and strategy generated.</p>
                </div>
            </div>
        </div>
    </div>

    <div class="vision-brief-container" v-if="visionStatus === 'generatedTheme'">
        <div class="generated-theme-wrapper">
            <div class="theme-header">
                <p class="static-qn">Generated Vision Themes</p>
                <p class="sub-txt">Select the vision that best captures your strategic ambition:</p>
            </div>

            <div class="theme-grid-container">
                <div class="grid-card" v-for="(suggestion, index) in suggestedArticles"
                    :class="{ active: suggestion.isSelected }" @click="selectSuggestion(index)">
                    <p v-if="suggestion.isSelected" class="selected-txt">SELECTED</p>
                    <p class="theme-title">{{ suggestion.title }}</p>
                    <p class="theme-description">{{ suggestion.statement }}</p>
                </div>
            </div>
        </div>
    </div>

    <div class="vision-brief-container" v-if="visionStatus === 'output'">
        <div class="output-wrapper">
            <div class="output-header">
                <div class="output-header-left">Generated Vision Output</div>

                <div v-if="!isEditingNarrative" class="output-header-right">
                    <button class="edit-btn" @click="editVision()">
                        <Svg :src="require('@/assets/icons/edit.svg')" fill="black"></Svg>
                    </button>
                </div>
                <div v-else class="output-header-right">
                    <button class="save-changes-btn" @click="saveVision" :disabled="previousVersion === editedVersion">
                        <Svg :src="require('@/assets/icons/save.svg')" fill="#262626"></Svg>
                        Save Changes
                    </button>
                    <button class="edit-btn" @click="cancelChanges">
                        <img :src="require('@/assets/icons/close.svg')"></img>
                    </button>
                </div>
            </div>
            <div class="output-details">
                <div class="output-details-wrapper" v-html="visionDetails" ref="detailsContainer" :key="refresh"
                    id="edit-container" v-if="!isEditingNarrative"></div>
                <div v-show="isEditingNarrative" class="editor-wrapper">
                    <div ref="editorContainer">
                        <div ref="editor" class="quill-editor"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <CommonPopup v-if="showCommonPopup" :showCommonPopup="showCommonPopup" :popupType="popupType"
        @confirm-action="handlePopupConfirmation" />
</template>

<script>
import axiosInstance from '@/axiosInterceptor';
import { Eventbus } from '@/plugins/eventBus';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import CommonPopup from '@/components/CommonPopup.vue';
import StartIcon from '@/assets/icons/star.svg';
import InputBrief from '@/assets/icons/input-brief.svg';
import TickIcon from '@/assets/icons/tick-icon-white.svg';
import InfoIcon from '@/assets/icons/clr-info-icon.svg';
import UpIcon from '@/assets/icons/up-icon.svg';
import DownIcon from '@/assets/icons/down-icon.svg';
import { nextTick } from 'vue';
import { marked } from 'marked';

export default {
    props: {
        modelValue: {
            type: Object
        }
    },
    components: {
        CommonPopup,
    },
    data() {
        return {
            InfoIcon, DownIcon, UpIcon,
            isFormValid: false,
            selectedQues: [],
            quesLoader: false,
            regenerateLoader: [],
            staticQuestion: {},
            dynamicQuestion: [],
            visionStatus: null,
            isEditingNarrative: false,
            visionDetails: null,
            previousVersion: null,
            editedVersion: null,
            editor: null,
            refresh: 0,
            showCommonPopup: false,
            popupType: null,
            StartIcon,
            InputBrief,
            TickIcon,
            selectedInput: null,
            autoGenerate: false,
            suggestedArticles: [],
            isOpen: true,
            questions: "What positive change are you driving? What transformation are you working toward? How will things be different when you succeed, and what's the big shift you're creating?",
        }
    },
    computed: {
        vision: {
            get() {
                return this.modelValue;
            }
        }
    },
    watch: {
        staticQuestion: {
            deep: true,
            handler(newValue) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    staticQues: newValue
                });
            }
        },
        'staticQuestion.0.answer': {
            deep: true,
            handler(newValue) {
                this.isFormValid = typeof newValue === 'string' && newValue.length > 5;
            }
        },
        dynamicQuestion: {
            deep: true,
            handler(newValue) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    dynamicQues: newValue
                });
            }
        },
        suggestedArticles: {
            deep: true,
            handler(newValue) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    visionThemes: newValue
                });
            }
        },
        editedVersion() {
            this.$emit('update:modelValue', {
                ...this.modelValue,
                generatedContent: this.editedVersion
            });
        },
        isFormValid() {
            this.$emit('update:isFormValid', this.isFormValid);
        },
        isEditingNarrative(newVal) {
            this.$emit('update:isEditingNarrative', newVal);
            if (newVal) {
                nextTick(() => {
                    const el = this.$refs.editor;

                    if (el) {
                        try {
                            this.editor = new Quill(el, {
                                theme: "snow",
                            });

                            // 🔐 Set HTML only after Quill is initialized
                            this.editor.root.innerHTML = this.visionDetails;

                            this.editor.on("text-change", () => {
                                this.editedVersion = this.editor?.root?.innerHTML || '';
                            });

                            this.refresh += 1;

                        } catch (err) {
                            console.error("Failed to initialize Quill:", err);
                        }
                    } else {
                        console.error("Quill container not found (ref missing).");
                    }
                });
            }
        }
    },
    methods: {
        toggleButton(selectedInput) {
            this.selectedInput = selectedInput;

            if (selectedInput === 'autoGenerate') {
                this.isFormValid = true;
                this.autoGenerate = true;
            } else {
                this.autoGenerate = false;
                this.isFormValid = false;
            }
            this.$emit('update:autoGenerate', this.autoGenerate);
        },

        handlePopupConfirmation(val) {
            this.showCommonPopup = false;
            if (val === 'yes') {
                if (this.popupType === 'withoutSave') {
                    this.isEditingNarrative = false;
                }
            }
        },
        async handleRegenerateQuestion(index) {
            this.regenerateLoader[index] = true;
        },
        editVision() {
            this.previousVersion = this.visionDetails;
            this.editedVersion = this.visionDetails;
            this.isEditingNarrative = !this.isEditingNarrative;
        },
        saveVision() {
            let payload = {
                narrativeId: this.$route.params.narrativeId,
                vision: {
                    visionContent: this.editedVersion
                },
                lastUpdatedStage: 'Vision'
            };
            axiosInstance.put('/narrative/saveDraft', payload)
                .then(() => {
                    this.previousVersion = this.editedVersion;
                    this.visionDetails = this.editedVersion;
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'save',
                        message: "Changes saved successfully"
                    })
                })
                .catch((error) => {
                    console.error(error);
                    Eventbus.emit('commonSnackbar', {
                        snackType: 'error',
                        message: "Failed to save changes"
                    })
                })
                .finally(() => {
                    this.isEditingNarrative = !this.isEditingNarrative;
                });
        },
        cancelChanges() {
            if (this.previousVersion === this.editedVersion) {
                this.isEditingNarrative = !this.isEditingNarrative;
            } else {
                this.popupType = 'withoutSave';
                this.showCommonPopup = true;
            }
        },

        selectSuggestion(index) {
            this.suggestedArticles = this.suggestedArticles.map(article =>
                article.isSelected ? { ...article, isSelected: false } : article
            );
            this.suggestedArticles[index].isSelected = true;
            this.isFormValid = true;
        },
        
    },
    mounted() {
        if (Object.keys(this.vision).length > 0) {

            if (this.vision.generatedContent) {
                this.visionStatus = 'output';
                this.visionDetails = this.vision.generatedContent
            } else if (this.vision.visionThemes) {
                this.visionStatus = 'generatedTheme';
                this.suggestedArticles = this.vision.visionThemes.map((item) => ({
                    ...item,
                    isSelected: false
                }));
                this.isFormValid = false;
            } else {
                this.visionStatus = 'static';
                this.staticQuestion = this.vision.staticQues;
            }
        } else {
            this.visionStatus = null;
        }

    }
}
</script>

<style scoped>
.vision-brief-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;
    padding: 24px 8px 40px 8px;
}


.static-qn-container {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    width: 65%;
    height: 100%;
}

.static-container {
    display: flex;
    flex-direction: column;
    font-weight: 600;
    color: var(--dark-grey-color);
    border-radius: 8px;
    height: fit-content;
    overflow: hidden;
    border: 1px solid #E5E5E5;
}

.qn-header {
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #EEEEEE;
    align-items: center;
    justify-content: space-between;
}

.header-left {
    display: flex;
    flex-direction: row;
    gap: 0.75rem;
}

.dropdown {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 1.8rem;
    height: 1.8rem;
    background: #FFFFFF;
    border: 1px solid #d4d4d4;
    border-radius: 0.5rem;
}

.header-txt {
    display: flex;
    align-items: center;
}

.qn-wrapper {
    display: flex;
    flex-direction: column;
    padding: 1rem 1.25rem;
    gap: 0.5rem;
    background-color: #F5F5F5;
    border-top: 1px solid #E5E5E5;
}

.qn-txt {
    color: #737373;
    font-weight: 400;
    font-size: 14px;
}

.static-qn-wrapper {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    height: 100%;
}

.vision-form {
    display: flex;
    flex-direction: column;
    background: #FFFFFF0A;
    border-radius: 12px;
    padding: 2rem;
    gap: 2rem;
}

.vision-static-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 2rem;
    border-radius: 12px;
    background: #FFFFFF;
    flex: 1;
}

.vision-form.selected {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
}

.top-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.generated-theme-wrapper {
    display: flex;
    flex-direction: column;
    gap: 3rem;
    height: 100%;
    padding: 1rem 4rem;
}

.static-qn {
    font-size: var(--header-font-size);
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
    width: 50%;
}

.sub-txt {
    font-size: 14px;
    font-weight: 400;
    color: #FFFFFF99;
    font-style: italic;
}

.theme-grid-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(430px, 1fr));
    gap: 1.5rem;
    overflow-y: auto;
    scrollbar-width: thin;
    overflow: visible;
}

.grid-card {
    background-color: #FFFFFF1F;
    border: 1px solid #FFFFFF1F;
    border-radius: 12px;
}

.grid-card.active {
    position: relative;
    overflow: visible;
    background: var(--primary-gradient-with-black) padding-box, var(--primary-gradient) border-box;
    border: 2px solid transparent;
    border-radius: 12px;
    background-clip: padding-box, border-box;
}

.selected-txt {
    position: absolute;
    top: -12px;
    left: 23px;
    color: #FFFFFF;
    z-index: 10;
    padding: 0.1rem 1.5rem;
    border-radius: 25rem;
    background: var(--primary-gradient);
    font-size: 12px;
}

.theme-title {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #FFFFFF33;
    font-weight: 600;
    letter-spacing: 0.2px;
    font-size: var(--normal-font-size);
    font-family: 'Inter';
    color: var(--secondary-font-color);
}

.theme-description {
    padding: 1rem 1.5rem;
    color: #FFFFFFCC;
    font-size: 14px;
    font-family: 'Inter';
    letter-spacing: 0.2px;
}

.output-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    height: 100%;
    width: 48%;
}

.bottom-container {
    width: 70%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 1rem;
}

.btn-wrapper {
    border: 1px solid var(--secondary-font-color);
    border-radius: 8px;
    padding: 0.5rem 1rem;
    height: 100%;
}

.btn-wrapper:hover {
    border-radius: 8px;
    background: var(--primary-gradient-with-white);
    border: 1px solid #FFFFFF00;
}

.toggle-input-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;

    color: var(--secondary-font-color);
    font-size: 14px;
}

.btn-wrapper.active {
    background: var(--primary-gradient-with-white);
    border: none;
}


.input-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    padding: 1rem 2rem;
    background: #FFFFFF1A;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.input-container.vision-brief {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 2rem;
    background: #FFFFFF0A;
    border-top: 2px solid #FFFFFF0A;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
}

.input-txt {
    color: var(--secondary-font-color);
}

.form-input {
    padding: 1rem;
    border-radius: 12px !important;
    background-color: var(--secondary-font-color);
    border: 1px solid var(--light-grey-color);
    width: 100%;
}

.header {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
    font-family: 'OverpassMono';
}

.vision-form-label {
    display: flex;
    flex-direction: column;
    gap: 1em;
}

.form-label {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding: 40px;
    border-radius: 24px;
    background: #FAFAFA;
}

.form-title {
    text-transform: uppercase;
    font-size: 14px;
    font-weight: 700;
    text-align: left;
    color: #A3A3A3;
}

.form-input {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.label {
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    color: #171717;
}

.label-bold {
    color: #171717;     
    font-weight: 600;
}

.label-italic {
    font-style: italic;
}

.dynamic-header {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.header-title {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
}

.header-name {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.header-subtitle {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    color: #E5E5E5;
}

.body {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.ques-ans-container {
    display: flex;
    gap: 16px;
    align-items: baseline;
}

.select-box {
    appearance: none;
    width: 20px;
    height: 20px;
    border: 2px solid white;
    border-radius: 2px;
    background-color: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.select-box:checked {
    background-color: #DF009E;
    border-color: #DF009E;
}

.select-box:checked::after {
    content: '';
    background-image: url('@/assets/icons/tick-icon.svg');
    background-size: cover;
    background-repeat: no-repeat;
    display: block;
    width: 12px;
    height: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.ques-ans-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid #D4D4D4;
    background: #FFFFFF;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    border-radius: 8px;
}

.ques-ans {
    display: flex;
    flex-direction: column;
    gap: 1em;
    padding: 1.2em 2em;
}

.ques-btn-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ques {
    color: #171717;
    font-size: 16px;
    font-weight: 500;
}

.ans-wrapper {
    border: 1px solid #D4D4D4;
    background: #F5F5F5;
    border-radius: 4px;
    padding: 20px;
    color: #262626;
    font-size: 16px;
    font-weight: 400;
}

.regenerate-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid #DF009E;
    color: #DF009E;
    border-radius: 8px;
}

.output-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.output-header-left {
    font-size: var(--header-font-size);
    font-weight: 600;
    text-align: left;
    color: #FAFAFA;
    font-family: 'san-serif', 'Inter';
}

.output-header-left.black {
    color: var(--dark-grey-color);
    font-family: 'san-serif', 'Inter';
    height: 4rem;
    border-bottom: 1px solid #DDDDDD;
}

.output-header-right {
    display: flex;
    gap: 1em;
}

.edit-btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid #A3A3A3;
    background: white;
}

.save-changes-btn {
    width: 11rem;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;
    gap: 0.7rem;
    border-radius: 1.5rem;
    background: white;
    color: #262626;
    border: 1px solid #A3A3A3;
}

.output-details {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    padding: 2rem;
    background-color: white;
    border-radius: 16px;
}

.output-details-wrapper{
    display: flex;
    flex-direction: column;
    gap: 1rem
}

.output-details-title {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    color: #171717;
}
</style>

<style>
.output-details-wrapper ol {
    padding-left: 50px !important;
}
</style>